import { useRef, useState } from "react"
import { useApi } from "../../hooks/useApi"
import { useWatchState } from "../../hooks/useWatchState"
import { IItem, IRowProps, isDeleted, itemTypes } from "../../stories/quoteOrders/quoteOrderUtils"
import { sum } from "../../utils/arrays"
import { useSettings } from "../../utils/useSettings"
import { calculateMarginFromPrice } from "../../utils/utils"

export function useBuildItemHooks({
  data,
  onChange,
  quote,
}: Pick<IRowProps, "data" | "onChange" | "quote">) {
  const api = useApi()
  const [calculatedTotalMax, setCalculatedTotalMax] = useState<number | null>(null)
  const totalMaxCountRef = useRef(calculatedTotalMax)
  totalMaxCountRef.current = calculatedTotalMax

  const { useExplicitAllocations, useQuoteOrderStandardPricing } = useSettings()

  const watchedItems = (
    quote.items.filter(
      item =>
        // not deleted
        !isDeleted(item) &&
        // parent is a bom item
        data.type === itemTypes.buildSet &&
        // this child is a bom sub item
        item.type === itemTypes.buildSetItem &&
        // it matches the set uid of the parent
        item.build_set_uid === data.build_set_uid &&
        // it's not the parent item
        item.id !== data.id,
    ) as IItem[]
  ).sort((a, b) => a.id.localeCompare(b.id))

  // Sync item prices
  useWatchState(
    (_, prices) => {
      if (data?.type !== itemTypes.buildSet) {
        return
      }
      console.log("SYNC CST PRICE")

      if (!quote.isInternalTransfer && data.product && useQuoteOrderStandardPricing) {
        return
      } else {
        // calculate normal prices
        const costPrice = sum(prices, price => (price.cost_price ?? 0) * (price.quantity ?? 1))
        const margin = calculateMarginFromPrice(costPrice, data.price_pennies ?? 0)
        // keep price - update cost price & margin
        onChange({ cost_price_pennies: costPrice, markUp: margin })
      }
    },
    watchedItems.map(item => ({
      standard_price_pennies: item.standard_price_pennies,
      standard_discount_percent: item.standard_discount_percent,
      cost_price: item.cost_price_pennies,
      price_pennies: item.price_pennies,
      quantity: item.quantity,
      type: item.type,
    })),
    250,
  )

  // calculate the max build for the product
  useWatchState(
    async (_, items) => {
      if (data?.type !== itemTypes.buildSet) {
        return
      }
      const productLocationIds = items.map(item => item.product_location).filter(Boolean)
      if (productLocationIds.length === 0) {
        setCalculatedTotalMax(0)
        return
      }
      const stock = await api.productLocationsStock(productLocationIds)

      const availableCounts: number[] = []
      items.forEach(item => {
        if (!item.product_location) {
          return
        }
        const stockData = stock[item.product_location]
        if (!stockData) {
          return
        }

        const requiredAmt = item.build_quantity ?? 1
        const availableAmt = stockData.inStock
        const maxAvailable = Math.floor(availableAmt / requiredAmt)
        availableCounts.push(maxAvailable)
      })

      setCalculatedTotalMax(Math.min(...availableCounts))
    },
    watchedItems
      .map(item => ({
        product_location: item.product_location?.id,
        build_quantity: item.build_quantity,
        allocated_quantity: item.allocated_quantity,
        useExplicitAllocations,
        type: item.type,
      }))
      .filter(x => x.product_location),
    250,
  )

  return {
    calculatedTotalMax,
    totalMaxCountRef,
    watchedItems,
  }
}
